import { Component, type ErrorInfo, type ReactNode } from "react";

type State = { error: Error | null };

/** A render error used to unmount the whole app into a blank page. Now it shows the message (for the bug report)
 * and offers a reload that also drops the service worker and its caches, so a stale bundle cannot persist. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };
  static getDerivedStateFromError(error: Error): State { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error("render error", error, info.componentStack); }
  async reload() {
    try {
      const regs = await navigator.serviceWorker?.getRegistrations?.();
      await Promise.all((regs ?? []).map((r) => r.unregister()));
      const keys = await caches?.keys?.();
      await Promise.all((keys ?? []).map((k) => caches.delete(k)));
    } catch { /* nothing to clear */ }
    window.location.reload();
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-lg p-8 text-sm">
        <h1 className="text-lg font-semibold">Something went wrong / Κάτι πήγε στραβά</h1>
        <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-surface-2 p-3 text-xs text-danger">{String(this.state.error?.message || this.state.error)}</pre>
        <button type="button" className="mt-4 rounded-ctl bg-accent px-4 py-2 text-white" onClick={() => this.reload()}>Reload / Επαναφόρτωση</button>
      </div>
    );
  }
}
